import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TYPE_LABEL_KEY, SKILL_LABEL_RU } from '@/api/ieltsMock';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { cn } from '@/lib/utils';

/**
 * ReviewItem — разбор одного вопроса, как его отдаёт сервер в поле review
 * попытки мока или спринта.
 */
export type ReviewItem = {
  question_id: string;
  skill: string;
  type: string;
  prompt: string;
  correct: boolean;
  student_answer: string;
  correct_answer: string;
  explanation?: string;
  evidence?: string;
  evidence_ref?: string;
};

/**
 * AnswerReview — разбор ответов после сдачи: что ученик написал, что было
 * верно, почему именно так и на чём это основано в тексте.
 *
 * Отдельный компонент, потому что нужен в трёх местах — спринт, мок и полный
 * тест, — и разъезжаться этим трём видам нельзя: ученик должен видеть один и
 * тот же разбор везде.
 *
 * По умолчанию показываются ТОЛЬКО ошибки: их обычно единицы, а верных
 * ответов десятки, и листать их ради двух промахов бессмысленно. Переключатель
 * открывает всё.
 */
export function AnswerReview({ items }: { items: ReviewItem[] }) {
  const { t } = useTranslation();
  const [onlyWrong, setOnlyWrong] = useState(true);

  const wrong = useMemo(() => items.filter((i) => !i.correct), [items]);
  const shown = onlyWrong ? wrong : items;

  if (items.length === 0) {
    return (
      <Card variant="subtle">
        <p className="text-sm text-on-surface-variant">{t('answerReview.unavailable')}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle>{t('answerReview.title')}</CardTitle>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-on-surface-variant">
            {t('answerReview.mistakes', { wrong: wrong.length, total: items.length })}
          </span>
          <button
            onClick={() => setOnlyWrong((v) => !v)}
            className="rounded-pill border border-outline px-3 py-1 text-sm font-medium hover:border-primary"
          >
            {t(onlyWrong ? 'answerReview.showAll' : 'answerReview.onlyWrong')}
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <Card variant="subtle" className="text-center">
          <p className="text-sm text-on-surface-variant">{t('answerReview.noMistakes')}</p>
        </Card>
      ) : (
        <ol className="space-y-3">
          {shown.map((r, i) => (
            <li
              key={r.question_id || i}
              className={cn(
                'rounded-2xl border p-4',
                r.correct
                  ? 'border-success/40 bg-success-container/30'
                  : 'border-error/40 bg-error-container/30',
              )}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Pill variant={r.correct ? 'success' : 'danger'}>
                  {t(r.correct ? 'answerReview.correct' : 'answerReview.wrong')}
                </Pill>
                <span className="text-xs text-on-surface-variant">
                  {SKILL_LABEL_RU[r.skill] ?? r.skill} · {t(TYPE_LABEL_KEY[r.type] ?? r.type)}
                </span>
              </div>

              <p className="text-[15px]">{r.prompt}</p>

              <p className="mt-2 text-sm">
                {t('answerReview.yourAnswer')} <b>{r.student_answer?.trim() || t('answerReview.skipped')}</b>
                {!r.correct && (
                  <>
                    {' · '}{t('ielts.correctAnswer')}: <b>{r.correct_answer}</b>
                  </>
                )}
              </p>

              {r.explanation && (
                <p className="mt-2 text-sm text-on-surface-variant">{r.explanation}</p>
              )}

              {/* Дословная опора из текста или транскрипта: ученик должен
                  видеть, ГДЕ в материале лежит ответ, а не верить на слово. */}
              {r.evidence && (
                <blockquote className="mt-2 border-l-2 border-outline pl-3 text-sm italic text-on-surface-variant">
                  «{r.evidence}»
                  {r.evidence_ref && <span className="not-italic"> — {r.evidence_ref}</span>}
                </blockquote>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
